import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { supabase } from '../../lib/supabase';
import { getServerSessionId } from '../../lib/serverSession';
import AdminCertificateCreator from './AdminCertificateCreator';
import { UserCertificateLayoutEditor } from './UserCertificateLayoutEditor';
import { LiveCertificateEditor } from './LiveCertificateEditor';
import ConsolidatedCertificateAdmin from './ConsolidatedCertificateAdmin';
import { certificateService } from '../../lib/certificateService';
import { svgCertificateGenerator } from '../../lib/svgCertificateGenerator';
import { sendKYCReminderEmail } from '../../lib/services/kycReminderService';
import { notificationService } from '../../lib/notificationService';
import { logAdminAction } from '../../lib/adminAuth';

// Escape HTML metacharacters so DB/user-sourced text cannot inject markup/script
// when written into a same-origin popup via document.write (DOM-XSS hardening).
// Order matters: '&' must be replaced first.
const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

interface Certificate {
  id: string;
  user_id: number;
  purchase_id: string;
  certificate_number: string; // Keep for backward compatibility
  cert_no: string; // New sequential certificate number
  shares_count: number;
  issue_date: string;
  status: string;
  certificate_data: any;
  approval_status?: string;
  approved_by?: number;
  approved_at?: string;
  rejected_reason?: string;
  admin_notes?: string;
  user_name?: string;
  user_email?: string;
  qr_code_url?: string; // QR code URL for certificate verification
  user_approved_at?: string; // Timestamp when user approved the certificate
  is_frozen?: boolean; // Indicates if the certificate is frozen
  freeze_reason?: string | null; // Admin-provided reason for freezing
  // Company certificate fields
  is_company_certificate?: boolean;
  company_id?: string;
  company_name?: string;
  company_registration_number?: string;
  user?: { // User object populated when loading certificates
    email: string;
    full_name: string;
  };
}

interface Purchase {
  id: string;
  user_id: number;
  package_name: string;
  shares_purchased: number;
  total_amount: number;
  status: string;
  created_at: string;
  user_email?: string;
  user_name?: string;
  has_kyc?: boolean; // NEW: KYC completion status
  kyc_status?: 'complete' | 'incomplete' | 'pending'; // NEW: Detailed KYC status
  user?: {
    email: string;
    full_name: string;
  };
  existing_certificate?: {
    id: string;
    cert_no: string;
    certificate_number: string;
    status: string;
  };
}

export const CertificateManagement: React.FC = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [pendingPurchases, setPendingPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedPurchases, setSelectedPurchases] = useState<Set<string>>(new Set());
  // Individual button states for each purchase
  const [processingPurchases, setProcessingPurchases] = useState<Set<string>>(new Set());
  // Separate flag for KYC-only refresh operations
  const [refreshingKyc, setRefreshingKyc] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'created' | 'issued' | 'consolidated' | 'company'>('consolidated');
  // NEW: Sub-tab for KYC filtering in 'list' tab
  // Only pending_kyc tab is shown now (All Purchases and Ready for Certificate removed)
  const [kycFilterTab, setKycFilterTab] = useState<'all' | 'ready' | 'pending_kyc'>('pending_kyc');
  // Bulk certificate selection for deletion
  const [selectedCertificates, setSelectedCertificates] = useState<Set<string>>(new Set());
  // Track which purchases have had KYC reminders sent with TIMESTAMPS (24-hour cooldown)
  // Store as Map: purchase_id -> timestamp_sent
  // Initialize from localStorage to persist across page refreshes
  const [kycRemindersSent, setKycRemindersSent] = useState<Map<string, number>>(() => {
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('kycRemindersSentWithTimestamps');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          // Filter out entries older than 24 hours
          const validEntries = Object.entries(parsed).filter(
            ([, timestamp]) => now - (timestamp as number) < TWENTY_FOUR_HOURS_MS
          );
          return new Map(validEntries as [string, number][]);
        } catch (e) {
          console.error('Error parsing kycRemindersSent from localStorage:', e);
        }
      }
      // Migrate old format (Set of IDs without timestamps) - treat all as expired
      const oldFormatStored = localStorage.getItem('kycRemindersSent');
      if (oldFormatStored) {
        try {
          // Clear old format
          localStorage.removeItem('kycRemindersSent');
          console.log('Migrated old kycRemindersSent format to new timestamp-based format');
        } catch (e) {
          // ignore
        }
      }
    }
    return new Map();
  });

  // Helper: Check if 24 hours have passed since reminder was sent
  const isReminderOnCooldown = (purchaseId: string): boolean => {
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const sentAt = kycRemindersSent.get(purchaseId);
    if (!sentAt) return false;
    return Date.now() - sentAt < TWENTY_FOUR_HOURS_MS;
  };

  // Helper: Get remaining cooldown time in hours
  const getRemainingCooldownHours = (purchaseId: string): number => {
    const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
    const sentAt = kycRemindersSent.get(purchaseId);
    if (!sentAt) return 0;
    const elapsed = Date.now() - sentAt;
    const remaining = TWENTY_FOUR_HOURS_MS - elapsed;
    return remaining > 0 ? Math.ceil(remaining / (60 * 60 * 1000)) : 0;
  };
  const [stats, setStats] = useState({
    total: 0,
    issued: 0,
    revoked: 0,
    totalShares: 0,
    totalValue: 0
  });

  // Created Certificates View State (Pending Approval)
  const [createdCertificates, setCreatedCertificates] = useState<any[]>([]);
  const [loadingCreated, setLoadingCreated] = useState(false);

  // Issued Certificates View State
  const [issuedCertificates, setIssuedCertificates] = useState<any[]>([]);
  const [filteredIssuedCerts, setFilteredIssuedCerts] = useState<any[]>([]);
  const [loadingIssued, setLoadingIssued] = useState(false);
  const [selectedIssuedCerts, setSelectedIssuedCerts] = useState<Set<string>>(new Set());

  // Certificate Viewer Modal State
  const [viewingCertificate, setViewingCertificate] = useState<Certificate | null>(null);
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Approval Workflow State
  const [showApprovalConfirm, setShowApprovalConfirm] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);

  // Per-user layout editor state (Created tab)
  const [showLayoutEditor, setShowLayoutEditor] = useState(false);
  const [layoutEditorCertificate, setLayoutEditorCertificate] = useState<Certificate | null>(null);

  const [liveEditorRefreshTrigger, setLiveEditorRefreshTrigger] = useState(0);

  // Address override editor state (within layout editor)
  const [addressOverride, setAddressOverride] = useState<{
    address_line1: string;
    address_line2: string;
    city: string;
    country: string;
    postal_code: string;
  } | null>(null);
  const [addressOverrideLoading, setAddressOverrideLoading] = useState(false);
  const [addressOverrideSaving, setAddressOverrideSaving] = useState(false);
  const [addressOverrideStatus, setAddressOverrideStatus] = useState<string | null>(null);

  // Reset All Certificates modal state (Issued tab)
  const [showResetAllModal, setShowResetAllModal] = useState(false);
  const [resetAllConfirmText, setResetAllConfirmText] = useState('');
  const [resetAllInProgress, setResetAllInProgress] = useState(false);
  const [resetAllStatusMessage, setResetAllStatusMessage] = useState<string | null>(null);

  // Freeze Certificates modal state
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeUsernameQuery, setFreezeUsernameQuery] = useState('');
  const [freezeSearchResults, setFreezeSearchResults] = useState<any[]>([]);
  const [freezeSelectedUser, setFreezeSelectedUser] = useState<any | null>(null);
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeInProgress, setFreezeInProgress] = useState(false);
  const [freezeStatusMessage, setFreezeStatusMessage] = useState<string | null>(null);




  // Filter State
  const [filters, setFilters] = useState({
    searchTerm: '',
    dateFrom: '',
    dateTo: '',
    sharesMin: '',
    sharesMax: '',
    sortBy: 'issue_date_desc' as 'issue_date_desc' | 'issue_date_asc' | 'cert_no_asc' | 'cert_no_desc' | 'shares_asc' | 'shares_desc',
    certificateType: 'all' as 'all' | 'individual' | 'company' // NEW: Filter by certificate type
  });

  // Company certificates state (separate list for company tab)
  const [companyCertificates, setCompanyCertificates] = useState<Certificate[]>([]);
  const [_loadingCompanyCerts, _setLoadingCompanyCerts] = useState(false);

  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'created') {
      loadCreatedCertificates();
    } else if (activeTab === 'issued') {
      loadIssuedCertificates();
    }
  }, [activeTab]);

  useEffect(() => {
    applyFilters();
  }, [filters, issuedCertificates]);

  // Persist kycRemindersSent (with timestamps) to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Convert Map to object for JSON serialization
      const obj = Object.fromEntries(kycRemindersSent);
      localStorage.setItem('kycRemindersSentWithTimestamps', JSON.stringify(obj));
    }
  }, [kycRemindersSent]);

  const loadStats = async () => {
    try {
      const certificateStats = await certificateService.getCertificateStats();
      setStats(certificateStats);
    } catch (error) {
      console.error('Error loading certificate stats:', error);
    }
  };

  // NOTE: fetchAllInBatches and fetchUsersByIds are now server-side in api/admin/certificates.js

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading certificates and purchases via API...');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_certificates', data: {} })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const certsWithUsers = (result.certificates || []).map((cert: any) => ({
        ...cert,
        user: { email: cert.user_email || 'Unknown', full_name: cert.user_name || 'Unknown User' }
      }));
      setCertificates(certsWithUsers);

      // Process pending purchases - filter for whole shares only
      const purchasesWithWholeShares = (result.pendingPurchases || []).filter((p: Purchase) => {
        const shares = p.shares_purchased;
        return shares >= 1 && shares === Math.floor(shares);
      }).map((p: any) => ({
        ...p,
        user: { email: p.user_email || 'Unknown', full_name: p.user_name || 'Unknown User' }
      }));

      setPendingPurchases(purchasesWithWholeShares);
      console.log(`✅ Loaded ${certsWithUsers.length} certificates and ${purchasesWithWholeShares.length} pending purchases`);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCreatedCertificates = async () => {
    setLoadingCreated(true);
    try {
      console.log('🔄 Loading created certificates (pending approval) via API...');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_created_certificates', data: {} })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const enrichedCerts = (result.certificates || []).map((cert: any) => ({
        ...cert,
        package_name: cert.certificate_data?.package_name || 'N/A'
      }));

      // CRITICAL FILTER: Only show certificates with whole shares (>= 1)
      const wholeShareCerts = enrichedCerts.filter((cert: Certificate) => {
        const shares = parseFloat(cert.shares_count.toString());
        return shares >= 1 && shares === Math.floor(shares);
      });

      console.log(`✅ Loaded ${wholeShareCerts.length} created certificates pending approval (whole shares only)`);
      setCreatedCertificates(wholeShareCerts);
    } catch (error) {
      console.error('Error loading created certificates:', error);
    } finally {
      setLoadingCreated(false);
    }
  };

  const loadIssuedCertificates = async () => {
    setLoadingIssued(true);
    try {
      console.log('🔄 Loading issued certificates via API...');
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'load_issued_certificates', data: {} })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      const enrichedCerts = (result.certificates || []).map((cert: any) => ({
        ...cert,
        package_name: cert.certificate_data?.package_name || 'N/A'
      }));

      // CRITICAL FILTER: Only show certificates with whole shares (>= 1)
      const wholeShareCerts = enrichedCerts.filter((cert: Certificate) => {
        const shares = parseFloat(cert.shares_count.toString());
        return shares >= 1 && shares === Math.floor(shares);
      });

      console.log(`✅ Loaded ${wholeShareCerts.length} issued certificates (whole shares only)`);

      // Count company certificates
      const companyCount = wholeShareCerts.filter((c: Certificate) => c.is_company_certificate).length;
      console.log(`🏢 ${companyCount} company certificates found`);

      setIssuedCertificates(wholeShareCerts);
      setFilteredIssuedCerts(wholeShareCerts);

      // Also populate company certificates list
      setCompanyCertificates(wholeShareCerts.filter((c: Certificate) => c.is_company_certificate));

    } catch (error) {
      console.error('❌ Error loading issued certificates:', error);
      alert('Error loading issued certificates');
    } finally {
      setLoadingIssued(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...issuedCertificates];

    // Certificate type filter (individual vs company)
    if (filters.certificateType === 'individual') {
      filtered = filtered.filter(cert => !cert.is_company_certificate);
    } else if (filters.certificateType === 'company') {
      filtered = filtered.filter(cert => cert.is_company_certificate);
    }
    // 'all' shows everything (no filter)

    // Search filter (user name, email, cert number, company name/registration)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(cert =>
        cert.user_name?.toLowerCase().includes(searchLower) ||
        cert.user_email?.toLowerCase().includes(searchLower) ||
        cert.cert_no?.toLowerCase().includes(searchLower) ||
        cert.certificate_number?.toLowerCase().includes(searchLower) ||
        cert.company_name?.toLowerCase().includes(searchLower) ||
        cert.company_registration_number?.toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(cert => new Date(cert.issue_date) >= fromDate);
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(cert => new Date(cert.issue_date) <= toDate);
    }

    // Share count range filter
    if (filters.sharesMin) {
      const min = parseInt(filters.sharesMin);
      filtered = filtered.filter(cert => cert.shares_count >= min);
    }
    if (filters.sharesMax) {
      const max = parseInt(filters.sharesMax);
      filtered = filtered.filter(cert => cert.shares_count <= max);
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'issue_date_desc':
          return new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime();
        case 'issue_date_asc':
          return new Date(a.issue_date).getTime() - new Date(b.issue_date).getTime();
        case 'cert_no_asc':
          return (a.cert_no || '').localeCompare(b.cert_no || '');
        case 'cert_no_desc':
          return (b.cert_no || '').localeCompare(a.cert_no || '');
        case 'shares_asc':
          return a.shares_count - b.shares_count;
        case 'shares_desc':
          return b.shares_count - a.shares_count;
        default:
          return 0;
      }
    });

    setFilteredIssuedCerts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      dateFrom: '',
      dateTo: '',
      sharesMin: '',
      sharesMax: '',
      sortBy: 'issue_date_desc',
      certificateType: 'all' // Reset certificate type filter
    });
  };

  const toggleIssuedCertSelection = (certId: string) => {
    setSelectedIssuedCerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(certId)) {
        newSet.delete(certId);
      } else {
        newSet.add(certId);
      }
      return newSet;
    });
  };

  const toggleAllIssuedCerts = () => {
    if (selectedIssuedCerts.size === filteredIssuedCerts.length) {
      setSelectedIssuedCerts(new Set());
    } else {
      setSelectedIssuedCerts(new Set(filteredIssuedCerts.map(cert => cert.id)));
    }
  };

  // NEW: Send KYC reminder email
  const sendKYCReminder = async (purchase: Purchase) => {
    if (!purchase.user || !purchase.user.email) {
      alert('❌ Cannot send reminder: User email not found');
      return;
    }

    const confirmed = confirm(
      `Send KYC reminder email to ${purchase.user.full_name || purchase.user.email}?\n\n` +
      `This will notify them that they need to complete KYC verification to receive their certificate.`
    );

    if (!confirmed) return;

    try {
      // Add purchase to processing state to disable button during send
      setProcessingPurchases(prev => new Set(prev).add(purchase.id));

      const result = await sendKYCReminderEmail({
        userId: purchase.user_id,
        userEmail: purchase.user.email,
        userName: purchase.user.full_name || 'Valued Customer',
        purchaseId: purchase.id,
        sharesCount: purchase.shares_purchased
      });

      if (result.success) {
        // Mark this purchase as having reminder sent WITH TIMESTAMP for 24-hour cooldown
        setKycRemindersSent(prev => {
          const newMap = new Map(prev);
          newMap.set(purchase.id, Date.now());
          return newMap;
        });
        alert('✅ KYC reminder email sent successfully! Button will be disabled for 24 hours.');
      } else {
        alert(`❌ Failed to send email: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error sending KYC reminder:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      // Remove from processing state
      setProcessingPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(purchase.id);
        return newSet;
      });
    }
  };

  const createCertificate = async (purchase: Purchase) => {
    // Check KYC status before creating certificate
    if (!purchase.has_kyc || purchase.kyc_status !== 'complete') {
      const sendReminder = confirm(
        `⚠️ Cannot generate certificate: User has not completed KYC verification.\n\n` +
        `Would you like to send them a KYC reminder email?`
      );

      if (sendReminder) {
        await sendKYCReminder(purchase);
      }
      return;
    }

    // Use PNG certificate creation (saves to database)
    await createPNGCertificate(purchase, true);
  };

  const createPNGCertificate = async (purchase: Purchase, saveToDb: boolean = true) => {
    // Add this purchase to processing set
    setProcessingPurchases(prev => new Set(prev).add(purchase.id));

    try {
      // First check if the purchase still exists in the database
      const sessionId = getServerSessionId();
      const checkResponse = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'check_purchase_exists', data: { purchaseId: purchase.id } })
      });
      const checkResult = await checkResponse.json();

      console.log('🔍 Purchase existence check:', {
        purchaseId: purchase.id,
        found: checkResult.exists ? 1 : 0
      });

      if (!checkResult.success || !checkResult.exists) {
        console.error('❌ Purchase record not found:', purchase.id);
        alert('⚠️ Cannot generate certificate: The purchase record no longer exists in the database. This may be an orphaned entry.');
        setProcessingPurchases(prev => {
          const newSet = new Set(prev);
          newSet.delete(purchase.id);
          return newSet;
        });
        return;
      }

      // Generate PNG certificate
      const result = await svgCertificateGenerator.generateCertificatePNG(purchase.id, saveToDb);

      if (!result.pngBlob) {
        throw new Error('Failed to generate PNG certificate');
      }

      const fileSize = (result.pngBlob.size / 1024 / 1024).toFixed(2);
      const message = saveToDb
        ? `✅ Certificate created and saved to database.\n📁 File size: ${fileSize}MB\n📜 Certificate #: ${result.certificateData?.certificateNumber}`
        : `✅ Test certificate generated.\n📁 File size: ${fileSize}MB`;

      alert(message);

      if (saveToDb) {
        // Send in-app notification to user
        try {
          await notificationService.createNotification({
            user_id: purchase.user_id,
            type: 'certificate',
            title: '📜 Certificate Being Created',
            message: 'Your shareholder certificate is being created and is pending admin approval. You will be notified once it is approved and ready for download.',
            metadata: {
              certificate_number: result.certificateData?.certificateNumber,
              shares_count: purchase.shares_purchased,
              status: 'pending_approval'
            }
          });
          console.log('✅ Certificate creation notification sent to user');
        } catch (notifError) {
          console.error('Error sending certificate creation notification:', notifError);
          // Don't fail the certificate creation if notification fails
        }

        await loadData(); // Refresh data
      }
    } catch (error) {
      console.error('Error creating PNG certificate:', error);
      alert(`❌ Error creating PNG certificate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Remove this purchase from processing set
      setProcessingPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(purchase.id);
        return newSet;
      });
    }
  };

  const previewCertificate = async (purchase: Purchase) => {
    // Add this purchase to processing set
    setProcessingPurchases(prev => new Set(prev).add(purchase.id));

    try {

      // Generate SVG certificate preview without saving to database
      const svgCertificate = await svgCertificateGenerator.generateCertificatePreview(purchase.id);

      if (!svgCertificate) {
        throw new Error('Failed to generate certificate preview');
      }

      // Open preview in new window
      const previewWindow = window.open('', '_blank', 'width=1200,height=800');
      if (previewWindow) {
        previewWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Certificate Preview - Purchase ${purchase.id}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  background: #f0f0f0;
                  font-family: Arial, sans-serif;
                }
                .container {
                  max-width: 1000px;
                  margin: 0 auto;
                  background: white;
                  padding: 20px;
                  border-radius: 8px;
                  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                }
                .header {
                  text-align: center;
                  margin-bottom: 20px;
                  padding-bottom: 10px;
                  border-bottom: 2px solid #D4AF37;
                }
                .certificate-preview {
                  text-align: center;
                }
                .actions {
                  margin-top: 20px;
                  text-align: center;
                }
                button {
                  margin: 0 10px;
                  padding: 10px 20px;
                  background: #D4AF37;
                  color: white;
                  border: none;
                  border-radius: 4px;
                  cursor: pointer;
                }
                button:hover {
                  background: #B8860B;
                }
              </style>
            </head>
            <body>
              <div class="container">
                <div class="header">
                  <h1>📜 Aureus Alliance Holdings Certificate Preview</h1>
                  <p>Purchase ID: ${escapeHtml(purchase.id)} | User: ${escapeHtml(purchase.user_name || 'N/A')}</p>
                </div>
                <div class="certificate-preview">
                  ${svgCertificate}
                </div>
                <div class="actions">
                  <button onclick="window.print()">🖨️ Print Certificate</button>
                  <button onclick="window.close()">✕ Close Preview</button>
                </div>
              </div>
            </body>
          </html>
        `);
        previewWindow.document.close();
      }

    } catch (error) {
      console.error('Error previewing certificate:', error);
      alert(`❌ Error previewing certificate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Remove this purchase from processing set
      setProcessingPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(purchase.id);
        return newSet;
      });
    }
  };

  const deleteCertificate = async (certificateId: string, purchaseId: string, certificateNumber?: string) => {
    const certDisplay = certificateNumber || certificateId;

    // Enhanced confirmation dialog
    if (!confirm(`⚠️ DELETE CERTIFICATE CONFIRMATION\n\nCertificate: ${certDisplay}\nPurchase ID: ${purchaseId}\n\nThis action will:\n• Permanently delete the certificate database record\n• Permanently delete the certificate PNG file from storage\n• Rollback share and certificate counters\n• Allow the purchase to have a new certificate created\n• This action CANNOT be undone\n\nAre you sure you want to proceed?`)) {
      return;
    }

    // Second confirmation with typed input
    const userInput = prompt(`To confirm deletion of certificate ${certDisplay}, type "DELETE" (case sensitive):`);
    if (userInput !== 'DELETE') {
      alert('❌ Deletion cancelled - confirmation text did not match');
      return;
    }

    // Add this purchase to processing set
    setProcessingPurchases(prev => new Set(prev).add(purchaseId));

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'undo_certificate', data: { certificateId } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error deleting certificate:', result.error);
        alert(`❌ Error deleting certificate: ${result.error}`);
      } else {
        alert(`✅ Certificate ${certDisplay} deleted successfully!\n\n• Database record removed\n• PNG file removed from storage\n• Counters rolled back (${result.rollbackShares} shares)\n\nThe purchase can now have a new certificate created with the same number.`);
        await loadData(); // Reload data to update UI
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      alert(`❌ Error deleting certificate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      // Remove this purchase from processing set
      setProcessingPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(purchaseId);
        return newSet;
      });
    }
  };

  const confirmCertificate = async (certificateId: string) => {
    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'confirm_certificate', data: { certificateId } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error confirming certificate:', result.error);
        alert('Error confirming certificate');
      } else {
        alert('✅ Certificate confirmed successfully');
        await loadData(); // Refresh data
      }
    } catch (error) {
      console.error('Error confirming certificate:', error);
      alert('Error confirming certificate');
    }
  };

  const rejectCertificate = async (certificateId: string) => {
    const reason = prompt('Please enter rejection reason (required):');

    if (!reason || !reason.trim()) {
      alert('Rejection cancelled: a reason is required.');
      return;
    }

    if (
      !confirm(
        '⚠️ Are you sure you want to reject this certificate?\n\n' +
          'This will:\n' +
          '• Mark the certificate as REJECTED\n' +
          '• Preserve the certificate number and share counters\n' +
          '• Keep the certificate record so you can fix the layout and regenerate later'
      )
    ) {
      return;
    }

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'reject_certificate', data: { certificateId, reason: reason.trim() } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error rejecting certificate:', result.error);
        alert('Error rejecting certificate');
      } else {
        console.log(
          `🚫 Certificate ${certificateId} marked as rejected. Reason: ${reason.trim()}`
        );
        alert(
          '🚫 Certificate marked as REJECTED.\n\n' +
            '• The certificate record is preserved\n' +
            '• Numbering and share counters were NOT changed\n' +
            'You can edit the layout and regenerate the PNG without creating a gap.'
        );
        await loadData(); // Refresh data
      }
    } catch (error) {
      console.error('Error rejecting certificate:', error);
      alert('Error rejecting certificate');
    }
  };

  const searchUsersForFreeze = async () => {
    if (!freezeUsernameQuery.trim()) {
      setFreezeStatusMessage('Please enter a username to search');
      return;
    }

    try {
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'search_user_for_freeze', data: { query: freezeUsernameQuery.trim() } })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('Error searching users for freeze:', result.error);
        setFreezeStatusMessage(`Error searching users: ${result.error}`);
        return;
      }

      setFreezeSearchResults(result.users || []);
      if (!result.users || result.users.length === 0) {
        setFreezeStatusMessage('No users found matching that username');
      } else {
        setFreezeStatusMessage(null);
      }
    } catch (err: any) {
      console.error('Unexpected error searching users for freeze:', err);
      setFreezeStatusMessage(`Unexpected error: ${err.message || String(err)}`);
    }
  };

  const freezeCertificatesForSelectedUser = async () => {
    if (!freezeSelectedUser) {
      setFreezeStatusMessage('Please select a user to freeze certificates for');
      return;
    }
    if (!freezeReason.trim()) {
      setFreezeStatusMessage('Please provide a reason for freezing the certificates');
      return;
    }

    const targetLabel = freezeSelectedUser.username || freezeSelectedUser.email || `User ID ${freezeSelectedUser.id}`;
    const confirmed = window.confirm(
      `Are you sure you want to FREEZE ALL certificates for ${targetLabel}?
\nThis will prevent the user from viewing or downloading any of their certificates until they are unfrozen.`
    );
    if (!confirmed) return;

    setFreezeInProgress(true);
    setFreezeStatusMessage(null);

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) {
        throw new Error('Not authenticated');
      }
      const adminEmail = authData?.user?.email || 'unknown-admin';

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'freeze_certificate',
          data: { userId: freezeSelectedUser.id, reason: freezeReason.trim() }
        })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      await logAdminAction(
        adminEmail,
        'FREEZE_CERTIFICATES',
        'certificates',
        String(freezeSelectedUser.id),
        {
          freeze_reason: freezeReason.trim(),
          username: freezeSelectedUser.username || null,
          email: freezeSelectedUser.email || null
        }
      );

      setFreezeStatusMessage(`✅ All certificates for ${targetLabel} have been frozen.`);

      // Refresh certificate lists to reflect new frozen state
      await loadCreatedCertificates();
      await loadIssuedCertificates();

      // Reset and close modal
      setFreezeUsernameQuery('');
      setFreezeSearchResults([]);
      setFreezeSelectedUser(null);
      setFreezeReason('');
      setShowFreezeModal(false);
    } catch (err: any) {
      console.error('Error freezing certificates for user:', err);
      setFreezeStatusMessage(`❌ Failed to freeze certificates: ${err.message || String(err)}`);
    } finally {
      setFreezeInProgress(false);
    }
  };

  const unfreezeCertificate = async (certificateId: string) => {
    const confirmed = window.confirm('Are you sure you want to UNFREEZE this certificate?');
    if (!confirmed) return;

    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) {
        throw new Error('Not authenticated');
      }
      const adminEmail = authData?.user?.email || 'unknown-admin';

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'unfreeze_certificate', data: { certificateId } })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      await logAdminAction(
        adminEmail,
        'UNFREEZE_CERTIFICATE',
        'certificates',
        certificateId
      );

      alert('✅ Certificate has been unfrozen');
      await loadCreatedCertificates();
      await loadIssuedCertificates();
    } catch (err: any) {
      console.error('Error unfreezing certificate:', err);
      alert(`❌ Failed to unfreeze certificate: ${err.message || String(err)}`);
    }
  };


  const bulkDeleteCertificates = async () => {
    if (selectedCertificates.size === 0) {
      alert('Please select certificates to delete');
      return;
    }

    const certificateCount = selectedCertificates.size;
    if (!confirm(`⚠️ BULK DELETE CONFIRMATION\n\nYou are about to delete ${certificateCount} certificate(s).\n\nThis action will:\n• Permanently delete all selected certificates\n• Rollback share and certificate counters\n• Allow those purchases to have new certificates created\n• Cannot be undone\n\nAre you sure you want to continue?`)) {
      return;
    }

    // Enhanced confirmation for bulk delete
    const userInput = prompt(`To confirm bulk deletion of ${certificateCount} certificates, type "DELETE ALL" (case sensitive):`);
    if (userInput !== 'DELETE ALL') {
      alert('❌ Bulk deletion cancelled - confirmation text did not match');
      return;
    }

    try {
      setCreating(true);
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'delete_all_pending',
          data: { certificateIds: Array.from(selectedCertificates) }
        })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      alert(`Bulk deletion completed!\n✅ Deleted: ${result.successCount}\n📊 Counters rolled back: ${result.rollbackCount}\n❌ Errors: ${result.errorCount}`);
      setSelectedCertificates(new Set());
      await loadData();
    } catch (error) {
      console.error('Error in bulk deletion:', error);
      alert('❌ Error in bulk certificate deletion');
    } finally {
      setCreating(false);
    }
  };

  const resetAllCertificates = async () => {
    if (resetAllInProgress) return;

    if (resetAllConfirmText.trim() !== 'RESET ALL CERTIFICATES') {
      setResetAllStatusMessage('❌ Confirmation text does not match. Please type "RESET ALL CERTIFICATES" exactly.');
      return;
    }

    try {
      setResetAllInProgress(true);
      setResetAllStatusMessage('⏳ Resetting all certificates. This may take a few moments...');

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'reset_all_certificates', data: {} })
      });
      const result = await response.json();

      if (!result.success) {
        setResetAllStatusMessage(`❌ ${result.error}`);
        return;
      }

      setResetAllStatusMessage('✅ Reset complete. Deleted ' + result.deletedCerts + ' certificate record' + (result.deletedCerts === 1 ? '' : 's') + ' and scheduled ' + result.deletedFiles + ' PNG file' + (result.deletedFiles === 1 ? '' : 's') + ' for deletion.');

      setSelectedCertificates(new Set());
      setSelectedIssuedCerts(new Set());
      await loadData();
      await loadIssuedCertificates();

      setTimeout(() => {
        setShowResetAllModal(false);
        setResetAllConfirmText('');
        setResetAllStatusMessage(null);
      }, 800);
    } catch (error) {
      console.error('Unexpected error during resetAllCertificates:', error);
      setResetAllStatusMessage('❌ Unexpected error during reset. Check console for details.');
    } finally {
      setResetAllInProgress(false);
    }
  };


  const __createBatchCertificates = async () => {
    if (selectedPurchases.size === 0) {
      alert('Please select purchases to create certificates for');
      return;
    }

    try {
      setCreating(true);
      let successCount = 0;
      let errorCount = 0;

      for (const purchaseId of selectedPurchases) {
        const purchase = pendingPurchases.find(p => p.id === purchaseId);
        if (!purchase) continue;

        try {
          // Generate PNG certificate and save to database
          const result = await svgCertificateGenerator.generateCertificatePNG(purchase.id, true);

          if (result.pngUrl) {
            console.log(`✅ Certificate created for purchase ${purchaseId}: ${result.pngUrl}`);
            successCount++;
          } else {
            console.error(`Failed to generate PNG for purchase ${purchaseId}`);
            errorCount++;
          }
        } catch (error) {
          console.error(`Error creating certificate for purchase ${purchaseId}:`, error);
          errorCount++;
        }
      }

      alert(`Batch creation complete: ${successCount} certificates created, ${errorCount} errors`);
      setSelectedPurchases(new Set());
      await loadData();
    } catch (error) {
      console.error('Error in batch creation:', error);
      alert('Error in batch certificate creation');
    } finally {
      setCreating(false);
    }
  };

  const __togglePurchaseSelection = (purchaseId: string) => {
    const newSelection = new Set(selectedPurchases);
    if (newSelection.has(purchaseId)) {
      newSelection.delete(purchaseId);
    } else {
      newSelection.add(purchaseId);
    }
    setSelectedPurchases(newSelection);
  };

  const __selectAllPurchases = () => {
    if (selectedPurchases.size === pendingPurchases.length) {
      setSelectedPurchases(new Set());
    } else {
      setSelectedPurchases(new Set(pendingPurchases.map(p => p.id)));
    }
  };

  const toggleCertificateSelection = (certificateId: string) => {
    const newSelection = new Set(selectedCertificates);
    if (newSelection.has(certificateId)) {
      newSelection.delete(certificateId);
    } else {
      newSelection.add(certificateId);
    }
    setSelectedCertificates(newSelection);
  };

  const selectAllCertificates = () => {
    if (selectedCertificates.size === certificates.length) {
      setSelectedCertificates(new Set());
    } else {
      setSelectedCertificates(new Set(certificates.map(c => c.id)));
    }
  };

  // View certificate in modal
  const viewCertificate = async (certificate: Certificate) => {
    setViewingCertificate(certificate);
    setShowViewerModal(true);
    setLoadingPreview(true);
    setCertificatePreviewUrl(null);

    try {
      // First try to get the PNG file URL from certificate_data
      const certificateData = certificate.certificate_data;

      if (certificateData?.file_url) {
        console.log('✅ Using PNG file URL from certificate_data:', certificateData.file_url);
        setCertificatePreviewUrl(certificateData.file_url);
        setLoadingPreview(false);
        return;
      }

      // If no file URL stored, try to (re)generate PNG certificate for this existing row
      if (certificate.purchase_id) {
        console.log(`🔄 Regenerating PNG certificate for purchase ${certificate.purchase_id}...`);

        // First check if the purchase still exists
        const sessionId = getServerSessionId();
        const checkResponse = await fetch('/api/admin/certificates', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId ? { 'X-Session-Id': sessionId } : {})
          },
          body: JSON.stringify({ operation: 'check_purchase_exists', data: { purchaseId: certificate.purchase_id } })
        });
        const checkResult = await checkResponse.json();

        if (!checkResult.success || !checkResult.exists) {
          console.error('❌ Purchase record not found:', certificate.purchase_id);
          alert(
            '⚠️ Cannot generate certificate PNG: The original purchase record no longer exists in the database. The certificate data is preserved, but PNG generation requires the purchase record.'
          );
          setLoadingPreview(false);
          return;
        }

        // Purchase exists, regenerate PNG without creating a new certificate record
        const result = await svgCertificateGenerator.regenerateExistingCertificatePNG({
          certificateNumber: certificate.cert_no || certificate.certificate_number,
          sharesCount: certificate.shares_count,
          purchaseId: certificate.purchase_id,
          userId: certificate.user_id,
          issueDate: certificate.issue_date as string | null
        });

        if (result.pngUrl) {
          console.log('✅ Certificate PNG regenerated and saved:', result.pngUrl);
          setCertificatePreviewUrl(result.pngUrl);
        } else {
          console.warn('⚠️ Failed to regenerate certificate PNG');
          alert('⚠️ Failed to regenerate certificate PNG. Please check the console for details.');
        }
      } else {
        console.warn('⚠️ No purchase_id available for certificate generation');
        alert('⚠️ Cannot generate certificate PNG: No purchase ID associated with this certificate.');
      }
    } catch (error) {
      console.error('❌ Error loading certificate preview:', error);
      alert(`❌ Error loading certificate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Approve certificate
  const approveCertificate = async () => {
    if (!viewingCertificate) return;

    setProcessingApproval(true);
    try {
      // Get current admin user from auth session (regular supabase client)
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error('[CertificateApproval] Auth error:', authError);
      }
      if (!user) {
        throw new Error('Not authenticated');
      }

      console.log('[CertificateApproval] Approving certificate:', viewingCertificate.cert_no);

      // Call API to approve certificate
      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'approve_certificate',
          data: {
            certificateId: viewingCertificate.id,
            adminEmail: user.email,
            adminNotes: adminNotes || null
          }
        })
      });
      const result = await response.json();

      if (!result.success) throw new Error(result.error);

      // Send approval email to user
      console.log('[CertificateApproval] Sending approval email to:', viewingCertificate.user_email);

      const emailResponse = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          emailType: 'certificate_approval',
          data: {
            email: viewingCertificate.user_email,
            fullName: viewingCertificate.user_name || 'Shareholder',
            certificateNumber: viewingCertificate.cert_no || viewingCertificate.certificate_number,
            sharesCount: viewingCertificate.shares_count,
            dashboardUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://aureus.africa'}/dashboard`
          }
        })
      });

      const emailResult = await emailResponse.json();

      if (!emailResult.success) {
        console.error('[CertificateApproval] Email failed:', emailResult.error);
        // Don't fail the approval if email fails
      } else {
        console.log('[CertificateApproval] Email sent successfully:', emailResult.messageId);
      }

      alert(`✅ Certificate ${viewingCertificate.cert_no} approved successfully!\n\n📧 Email notification sent to ${viewingCertificate.user_email}`);

      // Close modal and refresh data
      setShowViewerModal(false);
      setShowApprovalConfirm(false);
      setAdminNotes('');
      await loadIssuedCertificates();
      await loadData();
    } catch (error) {
      console.error('[CertificateApproval] Error:', error);
      alert(`Failed to approve certificate: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setProcessingApproval(false);
    }
  };

  // Reject certificate (approval workflow)
  const handleRejectCertificate = async () => {
    if (!viewingCertificate || !rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }

    setProcessingApproval(true);
    try {
      // Get current admin user from auth session (regular supabase client)
      const {
        data: { user },
        error: authError
      } = await supabase.auth.getUser();
      if (authError) {
        console.error('[CertificateRejection] Auth error:', authError);
      }
      if (!user) {
        throw new Error('Not authenticated');
      }

      console.log(
        '[CertificateRejection] Marking certificate as rejected:',
        viewingCertificate.cert_no
      );

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'reject_certificate_modal',
          data: {
            certificateId: viewingCertificate.id,
            reason: rejectionReason.trim(),
            adminEmail: user.email,
            adminNotes: adminNotes || null
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('[CertificateRejection] Error updating certificate:', result.error);
        throw new Error(result.error);
      }

      alert(
        `✅ Certificate ${viewingCertificate.cert_no} marked as REJECTED.\n\nReason: ${rejectionReason}\n\n` +
          'The certificate record, number and counters were preserved so you can fix the layout and regenerate.'
      );

      // Update local state and refresh lists
      setViewingCertificate((prev) =>
        prev
          ? {
              ...prev,
              approval_status: 'rejected',
              rejected_reason: rejectionReason.trim(),
              admin_notes: adminNotes || undefined
            }
          : prev
      );
      setShowRejectionModal(false);
      setRejectionReason('');
      setAdminNotes('');
      await loadIssuedCertificates();
      await loadData();
    } catch (error) {
      console.error('[CertificateRejection] Error:', error);
      alert(
        `Failed to reject certificate: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    } finally {
      setProcessingApproval(false);
    }
  };

  const downloadCertificate = async (certificate: Certificate) => {
    try {
      // Prefer the stored PNG URL on the certificate record
      let fileUrl = certificate.certificate_data?.file_url as string | undefined;

      // If not present, but this certificate is currently being viewed and has a
      // loaded preview, fall back to the in-memory preview URL
      if ((!fileUrl || typeof fileUrl !== 'string') && viewingCertificate && viewingCertificate.id === certificate.id && certificatePreviewUrl) {
        console.log('[CertificateDownload] Using in-memory preview URL for download');
        fileUrl = certificatePreviewUrl;
      }

      if (!fileUrl) {
        alert('Certificate file not found. Please refresh the preview or regenerate the certificate image first.');
        return;
      }

      // Fetch the certificate file
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch certificate');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${certificate.cert_no || certificate.certificate_number}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate');
    }
  };

  // Load or initialise the editable address text for the layout editor
  const loadAddressOverrideForCertificate = async (certificate: Certificate) => {
    if (!certificate) return;

    if (!certificate.purchase_id) {
      setAddressOverride(null);
      setAddressOverrideStatus(
        'No purchase ID is associated with this certificate, so address overrides cannot be saved.'
      );
      return;
    }

    try {
      setAddressOverrideLoading(true);
      setAddressOverrideStatus(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'load_address_override',
          data: { purchaseId: certificate.purchase_id, userId: certificate.user_id }
        })
      });
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error);
      }

      if (result.source === 'override') {
        setAddressOverrideStatus('Loaded saved address override for this certificate.');
      } else if (result.source === 'kyc') {
        setAddressOverrideStatus(
          'Loaded address from KYC as a starting point. Edit and save to override for this certificate only.'
        );
      } else {
        setAddressOverrideStatus('No saved address found. Enter address details below.');
      }

      const addr = result.address || {};
      setAddressOverride({
        address_line1: addr.address_line1 || '',
        address_line2: addr.address_line2 || '',
        city: addr.city || '',
        country: addr.country || '',
        postal_code: addr.postal_code || ''
      });
    } catch (error) {
      console.error('[AddressOverride] Unexpected error loading address override:', error);
      setAddressOverrideStatus('Failed to load address override. Check console for details.');
    } finally {
      setAddressOverrideLoading(false);
    }
  };

  // Persist the edited address override for this certificate and regenerate the preview PNG
  const handleSaveAddressOverride = async () => {
    if (!layoutEditorCertificate || !layoutEditorCertificate.purchase_id || !addressOverride) {
      return;
    }

    try {
      setAddressOverrideSaving(true);
      setAddressOverrideStatus(null);

      const sessionId = getServerSessionId();
      const response = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({
          operation: 'save_address_override',
          data: {
            purchaseId: layoutEditorCertificate.purchase_id,
            userId: layoutEditorCertificate.user_id,
            address: addressOverride
          }
        })
      });
      const result = await response.json();

      if (!result.success) {
        console.error('[AddressOverride] Error saving override:', result.error);
        setAddressOverrideStatus('❌ Failed to save address override.');
        return;
      }

      setAddressOverrideStatus('✅ Address override saved for this certificate.');

      // After saving the override, regenerate the PNG so the preview and stored file use the new text
      await refreshLayoutPreview(layoutEditorCertificate);
    } catch (error) {
      console.error('[AddressOverride] Unexpected error saving override:', error);
      setAddressOverrideStatus('❌ Unexpected error while saving address override.');
    } finally {
      setAddressOverrideSaving(false);
    }
  };

  // Generate a fresh PNG preview for the layout editor using the latest per-user overrides
  const refreshLayoutPreview = async (certificate: Certificate) => {
    if (!certificate) return;
    console.log(
      '[LayoutPreview] Refreshing preview for certificate:',
      certificate.cert_no || certificate.certificate_number,
      'user:',
      certificate.user_id
    );
    try {
      if (!certificate.purchase_id) {
        console.warn('[LayoutPreview] No purchase ID is associated with this certificate.');
        return;
      }

      const sessionId = getServerSessionId();
      const checkResponse = await fetch('/api/admin/certificates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId ? { 'X-Session-Id': sessionId } : {})
        },
        body: JSON.stringify({ operation: 'check_purchase_exists', data: { purchaseId: certificate.purchase_id } })
      });
      const checkResult = await checkResponse.json();

      if (!checkResult.success || !checkResult.exists) {
        console.error(
          '[LayoutPreview] Purchase record not found:',
          certificate.purchase_id
        );
        return;
      }

      // Regenerate the PNG for this specific certificate without creating a new record
      const result = await svgCertificateGenerator.regenerateExistingCertificatePNG({
        certificateNumber: certificate.cert_no || certificate.certificate_number,
        sharesCount: certificate.shares_count,
        purchaseId: certificate.purchase_id,
        userId: certificate.user_id,
        issueDate: certificate.issue_date as string | null
      });

      if (!result.pngUrl) {
        console.warn('[LayoutPreview] Failed to regenerate certificate PNG');
      }
    } catch (error) {
      console.error('[LayoutPreview] Error generating preview:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-white">Loading certificate data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">📜 Certificate Management</h2>
          <p className="text-white">Generate and manage professional share certificates</p>
        </div>

        {/* Certificate Stats */}
        <div className="grid grid-cols-4 gap-4 text-center">
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-2xl font-bold text-blue-400">{stats.total}</p>
            <p className="text-sm text-white">Total Certificates</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-2xl font-bold text-green-400">{stats.issued}</p>
            <p className="text-sm text-white">Issued</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-2xl font-bold text-yellow-400">{stats.totalShares.toLocaleString()}</p>
            <p className="text-sm text-white">Total Shares</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <p className="text-2xl font-bold text-purple-400">${stats.totalValue.toLocaleString()}</p>
            <p className="text-sm text-white">Total Value</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-800 rounded-lg p-1 overflow-x-auto items-center">
        <button
          onClick={() => setActiveTab('consolidated')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'consolidated'
              ? 'bg-yellow-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          🏆 Consolidated Certificates
        </button>
        <button
          onClick={() => setActiveTab('list')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'list'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          📋 Legacy List
        </button>
        <button
          onClick={() => setActiveTab('create')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'create'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          ⚡ Legacy Generator
        </button>
        <button
          onClick={() => setActiveTab('created')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'created'
              ? 'bg-yellow-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          ⏳ Created ({createdCertificates.length})
        </button>
        <button
          onClick={() => setActiveTab('issued')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'issued'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          📜 Legacy Issued
        </button>
        <button
          onClick={() => setActiveTab('company')}
          className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'company'
              ? 'bg-blue-600 text-white'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          🏢 Company ({companyCertificates.length})
        </button>
        <button
          onClick={() => setShowFreezeModal(true)}
          className="flex-none px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap text-blue-300 border border-blue-500/60 hover:text-white hover:bg-blue-600/20"
        >
          ❄️ Freeze Certificates
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'consolidated' ? (
        <ConsolidatedCertificateAdmin />
      ) : activeTab === 'company' ? (
        // Company Certificates View
        <div className="space-y-6">
          {/* Company Certificates Header */}
          <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
            <div className="flex items-center space-x-3">
              <span className="text-blue-400 text-2xl">🏢</span>
              <div>
                <h4 className="text-blue-400 font-semibold">Company Certificates</h4>
                <p className="text-blue-200 text-sm">
                  Certificates issued to company accounts. These are held by companies rather than individual shareholders.
                </p>
              </div>
            </div>
          </div>

          {/* Company Certificates Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-blue-400">{companyCertificates.length}</div>
              <div className="text-white text-sm">Total Company Certificates</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-green-400">
                {companyCertificates.reduce((sum, cert) => sum + cert.shares_count, 0).toLocaleString()}
              </div>
              <div className="text-white text-sm">Total Company Shares</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="text-3xl font-bold text-purple-400">
                {new Set(companyCertificates.map(c => c.company_id)).size}
              </div>
              <div className="text-white text-sm">Unique Companies</div>
            </div>
          </div>

          {/* Company Certificates Table */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            {loadingIssued ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-white">Loading company certificates...</p>
              </div>
            ) : companyCertificates.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4">🏢</div>
                <h3 className="text-xl font-semibold text-white mb-2">No Company Certificates</h3>
                <p className="text-white">No certificates have been issued to company accounts yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Certificate #
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Primary Director
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Shares
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Issue Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {companyCertificates.map((cert) => (
                      <tr key={cert.id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-white">CERT: {cert.cert_no || cert.certificate_number}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-blue-400">{cert.company_name || 'Unknown Company'}</div>
                            <div className="text-white text-sm">{cert.company_registration_number}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-white">{cert.user_name}</div>
                            <div className="text-white text-sm">{cert.user_email}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-green-400">
                              {cert.shares_count.toLocaleString()} shares
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300">
                            {new Date(cert.issue_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col space-y-1">
                            <span className="px-2 py-1 text-sm font-semibold rounded-full bg-green-900/30 text-green-400 border border-green-700 text-center">
                              {cert.status}
                            </span>
                            {cert.is_frozen && (
                              <span className="px-2 py-1 text-sm font-semibold rounded-full bg-blue-900/40 text-blue-300 border border-blue-500 text-center">
                                ❄️ FROZEN
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'create' ? (
        <AdminCertificateCreator />
      ) : activeTab === 'created' ? (
        // Created Certificates View (Pending Approval)
        <div className="space-y-6">
          {/* Status Alert */}
          <div className="bg-yellow-900/30 rounded-lg p-4 border border-yellow-700">
            <div className="flex items-center space-x-3">
              <span className="text-yellow-400 text-2xl">⏳</span>
              <div>
                <h4 className="text-yellow-400 font-semibold">Certificates Pending Approval</h4>
                <p className="text-yellow-200 text-sm">
                  These certificates have been created and are awaiting your review and approval.
                  Review each certificate and click "Approve" to make it available to users.
                </p>
              </div>
            </div>
          </div>

          {/* Created Certificates List */}
          {loadingCreated ? (
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
              <div className="text-white">Loading created certificates...</div>
            </div>
          ) : createdCertificates.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h3 className="text-xl font-semibold text-white mb-2">All Caught Up!</h3>
              <p className="text-white">No certificates pending approval.</p>
            </div>
          ) : (
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700 border-b border-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Certificate #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Shares</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Created</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {createdCertificates.map((cert) => (
                    <tr key={cert.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-white">{cert.user_name}</div>
                          <div className="text-white text-sm">{cert.user_email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-mono text-sm text-blue-400">
                          {cert.cert_no || cert.certificate_number}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-blue-400">
                          {cert.shares_count.toLocaleString()} shares
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-300">
                          {new Date(cert.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => viewCertificate(cert)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                          >
                            👁️ Preview
                          </button>
                          <button
                            onClick={() => {
                              setLayoutEditorCertificate(cert);
                              setShowLayoutEditor(true);
                              // Load the editable address text and current preview for this certificate
                              loadAddressOverrideForCertificate(cert);
                              refreshLayoutPreview(cert);
                            }}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded transition-colors"
                          >
                            ✏️ Edit Layout
                          </button>
                          <button
                            onClick={() => {
                              setViewingCertificate(cert);
                              setShowApprovalConfirm(true);
                            }}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded transition-colors"
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => {
                              setViewingCertificate(cert);
                              setShowRejectionModal(true);
                            }}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                          >
                            ✗ Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : activeTab === 'issued' ? (
        // Issued Certificates View
        <div className="space-y-6">
          {/* Filters Section */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">🔍 Filter & Search</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Search */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Search (Name, Email, Certificate #)
                </label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="Search certificates..."
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  From Date
                </label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  To Date
                </label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Certificate Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Certificate Type
                </label>
                <select
                  value={filters.certificateType}
                  onChange={(e) => handleFilterChange('certificateType', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Certificates</option>
                  <option value="individual">👤 Individual Only</option>
                  <option value="company">🏢 Company Only</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Sort By
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="issue_date_desc">Issue Date (Newest First)</option>
                  <option value="issue_date_asc">Issue Date (Oldest First)</option>
                  <option value="cert_no_asc">Certificate # (A-Z)</option>
                  <option value="cert_no_desc">Certificate # (Z-A)</option>
                  <option value="shares_desc">Shares (High to Low)</option>
                  <option value="shares_asc">Shares (Low to High)</option>
                </select>
              </div>

              {/* Share Range */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Min Shares
                </label>
                <input
                  type="number"
                  value={filters.sharesMin}
                  onChange={(e) => handleFilterChange('sharesMin', e.target.value)}
                  placeholder="Min"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Max Shares
                </label>
                <input
                  type="number"
                  value={filters.sharesMax}
                  onChange={(e) => handleFilterChange('sharesMax', e.target.value)}
                  placeholder="Max"
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Clear Filters Button */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-white">
              Showing {filteredIssuedCerts.length} of {issuedCertificates.length} certificates
            </div>
          </div>

          {/* Issued Certificates Table */}
          <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <h3 className="text-lg font-semibold text-white">📜 Issued Certificates</h3>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  onClick={toggleAllIssuedCerts}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                >
                  {selectedIssuedCerts.size === filteredIssuedCerts.length ? 'Deselect All' : 'Select All'}
                </button>
                {selectedIssuedCerts.size > 0 && (
                  <button
                    onClick={() => alert(`Bulk actions for ${selectedIssuedCerts.size} certificates`)}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                  >
                    Delete {selectedIssuedCerts.size} Selected
                  </button>
                )}
                <button
                  onClick={() => {
                    setResetAllStatusMessage(null);
                    setResetAllConfirmText('');
                    setShowResetAllModal(true);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-700 via-red-600 to-red-700 hover:from-red-600 hover:to-red-500 text-white rounded-lg text-sm font-semibold transition-colors border border-red-500/80 shadow-sm flex items-center space-x-2"
                >
                  <span>⚠️</span>
                  <span>Reset All Certificates</span>
                </button>
              </div>
            </div>

            {loadingIssued ? (
              <div className="flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
                  <p className="text-white">Loading issued certificates...</p>
                </div>
              </div>
            ) : filteredIssuedCerts.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-white text-lg">No issued certificates found</p>
                <p className="text-gray-500 text-sm mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Select
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Certificate #
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Shares
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Issue Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-white uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {filteredIssuedCerts.map((cert) => (
                      <tr key={cert.id} className="hover:bg-gray-750 transition-colors">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selectedIssuedCerts.has(cert.id)}
                            onChange={() => toggleIssuedCertSelection(cert.id)}
                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-white">CERT: {cert.cert_no || cert.certificate_number}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-white">{cert.user_name}</div>
                              {cert.is_company_certificate && (
                                <span className="px-2 py-0.5 bg-blue-900/30 text-blue-400 text-sm rounded-full border border-blue-700">
                                  🏢 Company
                                </span>
                              )}
                            </div>
                            <div className="text-white text-sm">{cert.user_email}</div>
                            {cert.is_company_certificate && cert.company_name && (
                              <div className="text-blue-300 text-sm mt-1">
                                {cert.company_name} • {cert.company_registration_number}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-blue-400">
                              {cert.shares_count.toLocaleString()} shares
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-300">
                            {new Date(cert.issue_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col space-y-1">
                            <span className="px-2 py-1 text-sm font-semibold rounded-full bg-green-900/30 text-green-400 border border-green-700 text-center">
                              {cert.status}
                            </span>
                            {cert.approval_status && (
                              <span className={`px-2 py-1 text-sm font-semibold rounded-full text-center ${
                                cert.approval_status === 'approved'
                                  ? 'bg-green-900/30 text-green-400 border border-green-700'
                                  : cert.approval_status === 'rejected'
                                  ? 'bg-red-900/30 text-red-400 border border-red-700'
                                  : 'bg-yellow-900/30 text-yellow-400 border border-yellow-700'
                              }`}>
                                {cert.approval_status === 'pending_approval' ? '⏳ Pending' :
                                 cert.approval_status === 'approved' ? '✓ Approved' :
                                 '✗ Rejected'}
                              </span>
                            )}
                            {cert.is_frozen && (
                              <span className="px-2 py-1 text-sm font-semibold rounded-full bg-blue-900/40 text-blue-300 border border-blue-500 text-center">
                                ❄️ FROZEN
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => viewCertificate(cert)}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded transition-colors"
                              title="View & Approve Certificate"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => downloadCertificate(cert)}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                              title="Download Certificate"
                            >
                              📥 Download
                            </button>
                            {cert.is_frozen && (
                              <button
                                onClick={() => unfreezeCertificate(cert.id)}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded transition-colors"
                                title="Unfreeze this certificate"
                              >
                                🔓 Unfreeze
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
        {/* Enhanced Statistics Dashboard */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-700">
            <div className="text-2xl font-bold text-blue-400">{certificates.length}</div>
            <div className="text-blue-200">📜 Total Certificates</div>
            <div className="text-sm text-blue-300 mt-1">Issued & Active</div>
          </div>
          <div className="bg-orange-900/30 rounded-lg p-4 border border-orange-700">
            <div className="text-2xl font-bold text-orange-400">{pendingPurchases.length}</div>
            <div className="text-orange-200">⏳ Pending Certificates</div>
            <div className="text-sm text-orange-300 mt-1">Ready to Create</div>
          </div>
          <div className="bg-green-900/30 rounded-lg p-4 border border-green-700">
            <div className="text-2xl font-bold text-green-400">
              {certificates.reduce((sum, cert) => sum + cert.shares_count, 0).toLocaleString()}
            </div>
            <div className="text-green-200">📊 Total Certified Shares</div>
            <div className="text-sm text-green-300 mt-1">Across All Certificates</div>
          </div>
          <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700">
            <div className="text-2xl font-bold text-purple-400">{processingPurchases.size}</div>
            <div className="text-purple-200">⚡ Active Operations</div>
            <div className="text-sm text-purple-300 mt-1">Currently Processing</div>
          </div>
        </div>

        {/* System Status Indicator */}
        {(creating || processingPurchases.size > 0) && (
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
              <div>
                <div className="text-blue-400 font-semibold">🔄 System Processing</div>
                <div className="text-sm text-blue-300">
                  {creating && "Batch operations in progress..."}
                  {processingPurchases.size > 0 && `${processingPurchases.size} individual operation(s) running...`}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KYC Filter Tabs */}
      {pendingPurchases.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex space-x-2">
              {/* Only showing Pending KYC tab - All Purchases and Ready for Certificate removed */}
              <button
                onClick={() => setKycFilterTab('pending_kyc')}
                className="px-4 py-2 rounded-lg font-medium transition-colors bg-orange-600 text-white"
              >
                ⚠️ Pending KYC ({pendingPurchases.filter(p => !p.has_kyc || p.kyc_status !== 'complete').length})
              </button>
            </div>

            {/* KYC refresh button */}
            <button
              onClick={async () => {
                setRefreshingKyc(true);
                try {
                  await loadData();
                } finally {
                  setRefreshingKyc(false);
                }
              }}
              disabled={loading || refreshingKyc}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors flex items-center gap-2 ${
                refreshingKyc
                  ? 'bg-gray-700 text-gray-300 border-gray-600 cursor-wait'
                  : 'bg-gray-900 text-gray-100 border-gray-600 hover:bg-gray-800'
              }`}
              title="Re-check KYC status for all pending purchases"
            >
              <span>{refreshingKyc ? '⏳ Checking KYC...' : '🔄 Refresh KYC Status'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Pending Purchases - Need Certificates */}
      {(() => {
        // Filter purchases based on KYC tab
        const filteredPurchases = pendingPurchases.filter(p => {
          if (kycFilterTab === 'ready') {
            return p.has_kyc && p.kyc_status === 'complete';
          } else if (kycFilterTab === 'pending_kyc') {
            return !p.has_kyc || p.kyc_status !== 'complete';
          }
          return true; // 'all' tab
        });

        if (filteredPurchases.length === 0) return null;

        return (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">
              ⚠️ Pending KYC Verification
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-2 text-white">KYC Status</th>
                  <th className="text-left p-2 text-white">User</th>
                  <th className="text-left p-2 text-white">Package</th>
                  <th className="text-left p-2 text-white">Shares</th>
                  <th className="text-left p-2 text-white">Amount</th>
                  <th className="text-left p-2 text-white">Date</th>
                  <th className="text-left p-2 text-white">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPurchases.map((purchase) => {
                  const hasKyc = purchase.has_kyc && purchase.kyc_status === 'complete';

                  return (
                  <tr key={purchase.id} className="border-b border-gray-700/50">
                    <td className="p-2">
                      {hasKyc ? (
                        <span className="inline-flex items-center px-2 py-1 bg-green-900/30 text-green-400 rounded text-sm border border-green-700">
                          ✅ KYC Complete
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-red-900/30 text-red-400 rounded text-sm border border-red-700">
                          ⚠️ KYC Missing
                        </span>
                      )}
                    </td>
                    <td className="p-2 text-white">
                      <div className="flex items-center space-x-2">
                        <div>
                          <div>{purchase.user?.full_name || 'N/A'}</div>
                          <div className="text-sm text-white">{purchase.user?.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 text-white">{purchase.package_name}</td>
                    <td className="p-2 text-white">{purchase.shares_purchased.toLocaleString()}</td>
                    <td className="p-2 text-white">${purchase.total_amount.toLocaleString()}</td>
                    <td className="p-2 text-white">
                      {new Date(purchase.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-2">
                        {hasKyc ? (
                          <>
                            <button
                              onClick={() => createCertificate(purchase)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                              disabled={processingPurchases.has(purchase.id)}
                            >
                              {processingPurchases.has(purchase.id) ? 'Creating...' : '📜 Create Certificate'}
                            </button>
                            <button
                              onClick={() => previewCertificate(purchase)}
                              className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                              disabled={processingPurchases.has(purchase.id)}
                              title="Preview Certificate"
                            >
                              👁️ Preview
                            </button>
                          </>
                        ) : (
                          <>
                            {/* Only show button if NOT on 24-hour cooldown - completely hidden when cooling down */}
                            {!isReminderOnCooldown(purchase.id) ? (
                              <button
                                onClick={() => sendKYCReminder(purchase)}
                                disabled={processingPurchases.has(purchase.id)}
                                className={`px-3 py-1 text-white rounded text-sm transition-colors ${
                                  processingPurchases.has(purchase.id)
                                    ? 'bg-orange-400 cursor-wait'
                                    : 'bg-orange-600 hover:bg-orange-700'
                                }`}
                                title={
                                  processingPurchases.has(purchase.id)
                                    ? 'Sending reminder...'
                                    : 'Send KYC reminder email to user'
                                }
                              >
                                {processingPurchases.has(purchase.id)
                                  ? '📤 Sending...'
                                  : '📧 Send KYC Reminder'}
                              </button>
                            ) : (
                              <span
                                className="px-3 py-1 bg-green-700/50 text-green-300 rounded text-sm inline-flex items-center"
                                title={`Reminder sent - can resend in ${getRemainingCooldownHours(purchase.id)} hours`}
                              >
                                ✅ Sent ({getRemainingCooldownHours(purchase.id)}h)
                              </span>
                            )}
                            <button
                              onClick={() => createCertificate(purchase)}
                              className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 opacity-50 cursor-not-allowed"
                              disabled={true}
                              title="Cannot create certificate - KYC required"
                            >
                              🔒 KYC Required
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

        {/* Existing Certificates */}
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-white">📋 Issued Certificates</h3>
            {certificates.length > 0 && (
              <div className="space-x-2">
                <button
                  onClick={selectAllCertificates}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  disabled={creating}
                >
                  {selectedCertificates.size === certificates.length ? 'Deselect All' : 'Select All'}
                </button>
                <button
                  onClick={bulkDeleteCertificates}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={creating || selectedCertificates.size === 0}
                >
                  {creating ? 'Deleting...' : `Delete ${selectedCertificates.size} Selected`}
                </button>
              </div>
            )}
          </div>

        {certificates.length === 0 ? (
          <p className="text-white">No certificates issued yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left p-2 text-white">Select</th>
                  <th className="text-left p-2 text-white">Certificate #</th>
                  <th className="text-left p-2 text-white">User</th>
                  <th className="text-left p-2 text-white">Shares</th>
                  <th className="text-left p-2 text-white">Issue Date</th>
                  <th className="text-left p-2 text-white">Status</th>
                  <th className="text-left p-2 text-white">User Approval</th>
                  <th className="text-left p-2 text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((certificate) => (
                  <tr key={certificate.id} className="border-b border-gray-700/50">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedCertificates.has(certificate.id)}
                        onChange={() => toggleCertificateSelection(certificate.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-2 text-white font-mono">
                      <div className="text-sm">CERT: {certificate.cert_no || certificate.certificate_number}</div>
                    </td>
                    <td className="p-2 text-white">
                      <div>{certificate.user?.full_name || 'N/A'}</div>
                      <div className="text-sm text-white">{certificate.user?.email}</div>
                    </td>
                    <td className="p-2 text-white">{certificate.shares_count.toLocaleString()}</td>
                    <td className="p-2 text-white">
                      {new Date(certificate.issue_date).toLocaleDateString()}
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-sm ${
                        certificate.status === 'issued'
                          ? 'bg-green-900/30 text-green-400 border border-green-700'
                          : certificate.status === 'pending_review'
                          ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-700'
                          : 'bg-gray-900/30 text-white border border-gray-700'
                      }`}>
                        {certificate.status === 'pending_review' ? 'Pending Review' : certificate.status}
                      </span>
                    </td>
                    <td className="p-2">
                      {certificate.user_approved_at ? (
                        <span className="px-2 py-1 rounded text-sm bg-green-900/30 text-green-400 border border-green-700">
                          ✓ User Approved
                        </span>
                      ) : certificate.approval_status === 'approved' ? (
                        <span className="px-2 py-1 rounded text-sm bg-yellow-900/30 text-yellow-400 border border-yellow-700">
                          ⏳ Pending User
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-sm bg-gray-900/30 text-white border border-gray-700">
                          - N/A -
                        </span>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex space-x-2">
                        {certificate.status === 'pending_review' ? (
                          <>
                            <button
                              onClick={() => confirmCertificate(certificate.id)}
                              className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                              title="Confirm Certificate"
                            >
                              ✅ Confirm
                            </button>
                            <button
                              onClick={() => rejectCertificate(certificate.id)}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                              title="Reject Certificate"
                            >
                              ❌ Reject
                            </button>
                          </>
                        ) : (
                          <>
                            {certificate.certificate_data?.file_url ? (
                              <button
                                onClick={() => downloadCertificate(certificate)}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                              >
                                📥 Download
                              </button>
                            ) : (
                              <button
                                onClick={() => viewCertificate(certificate)}
                                disabled={processingPurchases.has(certificate.purchase_id)}
                                className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                                title="Generate PNG certificate file"
                              >
                                {processingPurchases.has(certificate.purchase_id) ? '⏳ Creating...' : '📄 Create PNG'}
                              </button>
                            )}
                            <button
                              onClick={() => deleteCertificate(certificate.id, certificate.purchase_id, certificate.cert_no || certificate.certificate_number)}
                              disabled={processingPurchases.has(certificate.purchase_id)}
                              className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
                              title="Delete Certificate"
                            >
                              {processingPurchases.has(certificate.purchase_id) ? '⏳ Deleting...' : '🗑️ Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
        </div>
      )}

      {/* Certificate Viewer Modal */}
      {showViewerModal && viewingCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex justify-between items-center z-10">
              <div>
                <h3 className="text-2xl font-bold text-white">📜 Certificate Review</h3>
                <p className="text-white text-sm mt-1">
                  Certificate: {viewingCertificate.cert_no || viewingCertificate.certificate_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowViewerModal(false);
                  setAdminNotes('');
                  setRejectionReason('');
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Certificate Details */}
              <div className="grid grid-cols-2 gap-6">
                <div className="bg-gray-750 rounded-lg p-4 border border-gray-700">
                  <h4 className="text-white font-semibold mb-3">👤 User Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white">Name:</span>
                      <span className="text-white font-medium">{viewingCertificate.user_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Email:</span>
                      <span className="text-white font-medium">{viewingCertificate.user_email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">User ID:</span>
                      <span className="text-white font-medium">#{viewingCertificate.user_id}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-750 rounded-lg p-4 border border-gray-700">
                  <h4 className="text-white font-semibold mb-3">📋 Certificate Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white">Cert No:</span>
                      <span className="text-white font-medium">{viewingCertificate.cert_no || viewingCertificate.certificate_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Shares:</span>
                      <span className="text-blue-400 font-bold">{viewingCertificate.shares_count.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white">Issue Date:</span>
                      <span className="text-white font-medium">
                        {new Date(viewingCertificate.issue_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Status */}
              <div className="bg-gray-750 rounded-lg p-4 border border-gray-700">
                <h4 className="text-white font-semibold mb-3">✓ Approval Status</h4>
                <div className="flex items-center space-x-4">
                  <span className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    viewingCertificate.approval_status === 'approved'
                      ? 'bg-green-900/30 text-green-400 border border-green-700'
                      : viewingCertificate.approval_status === 'rejected'
                      ? 'bg-red-900/30 text-red-400 border border-red-700'
                      : 'bg-yellow-900/30 text-yellow-400 border border-yellow-700'
                  }`}>
                    {viewingCertificate.approval_status === 'pending_approval' ? '⏳ Pending Approval' :
                     viewingCertificate.approval_status === 'approved' ? '✓ Approved' :
                     viewingCertificate.approval_status === 'rejected' ? '✗ Rejected' :
                     '⏳ Pending Approval'}
                  </span>
                  {viewingCertificate.approved_at && (
                    <span className="text-white text-sm">
                      on {new Date(viewingCertificate.approved_at).toLocaleString()}
                    </span>
                  )}
                </div>
                {viewingCertificate.rejected_reason && (
                  <div className="mt-3 p-3 bg-red-900/20 border border-red-700 rounded">
                    <p className="text-red-400 text-sm">
                      <strong>Rejection Reason:</strong> {viewingCertificate.rejected_reason}
                    </p>
                  </div>
                )}
                {viewingCertificate.admin_notes && (
                  <div className="mt-3 p-3 bg-blue-900/20 border border-blue-700 rounded">
                    <p className="text-blue-400 text-sm">
                      <strong>Admin Notes:</strong> {viewingCertificate.admin_notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Certificate Preview */}
              <div className="bg-gray-750 rounded-lg p-4 border border-gray-700">
                <h4 className="text-white font-semibold mb-3">📄 Certificate Preview</h4>
                {loadingPreview ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
                    <span className="ml-3 text-gray-300">Loading preview...</span>
                  </div>
                ) : certificatePreviewUrl ? (
                  <div className="space-y-3">
                    <div className="bg-white rounded border border-gray-300 overflow-auto" style={{ maxHeight: '500px' }}>
                      <img
                        src={certificatePreviewUrl}
                        alt="Certificate Preview"
                        className="w-full h-auto"
                        style={{ minHeight: '500px', objectFit: 'contain' }}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => {
                          if (certificatePreviewUrl) {
                            window.open(certificatePreviewUrl, '_blank');
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-2"
                      >
                        🔍 View Full Size
                      </button>
                      <button
                        onClick={() => downloadCertificate(viewingCertificate)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-2"
                      >
                        📥 Download Certificate
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white mb-4">No preview available</p>
                    <p className="text-gray-500 text-sm">The certificate PNG file could not be loaded.</p>
                  </div>
                )}
              </div>

              {/* Admin Notes Input (for approval/rejection) */}
              {viewingCertificate.approval_status !== 'approved' && (
                <div className="bg-gray-750 rounded-lg p-4 border border-gray-700">
                  <h4 className="text-white font-semibold mb-3">📝 Admin Notes (Optional)</h4>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add any notes about this certificate..."
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                    rows={3}
                  />
                </div>
              )}
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowViewerModal(false);
                  setAdminNotes('');
                  setRejectionReason('');
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
              >
                Close
              </button>

              {viewingCertificate.approval_status !== 'approved' && (
                <>
                  <button
                    onClick={() => setShowRejectionModal(true)}
                    className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                  >
                    ✗ Reject
                  </button>
                  <button
                    onClick={() => setShowApprovalConfirm(true)}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors font-semibold"
                  >
                    ✓ Approve Certificate
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Layout Editor Modal */}
      {showLayoutEditor && layoutEditorCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-900 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-gray-700 p-4 flex justify-between items-center z-10">
              <div>
                <h3 className="text-xl font-bold text-white">✏️ Edit Certificate Layout</h3>
                <p className="text-white text-sm mt-1">
                  {layoutEditorCertificate.user_name} • Cert{' '}
                  {layoutEditorCertificate.cert_no || layoutEditorCertificate.certificate_number}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowLayoutEditor(false);
                  setLayoutEditorCertificate(null);
                  setAddressOverride(null);
                  setAddressOverrideStatus(null);
                  setAddressOverrideLoading(false);
                  setAddressOverrideSaving(false);
                }}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ✕
              </button>
            </div>

            {/* Modal Content - Live SVG Certificate Editor */}
            <div className="p-4">
              <LiveCertificateEditor
                certificateNumber={layoutEditorCertificate.cert_no || layoutEditorCertificate.certificate_number}
                sharesCount={layoutEditorCertificate.shares_count}
                purchaseId={layoutEditorCertificate.purchase_id || ''}
                userId={layoutEditorCertificate.user_id}
                issueDate={layoutEditorCertificate.issue_date as string | null}
                onAddressChange={(address) => {
                  console.log('Address changed:', address);
                  setLiveEditorRefreshTrigger(prev => prev + 1);
                }}
                refreshTrigger={liveEditorRefreshTrigger}
              />
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700" style={{display: 'none'}}>
                <h4 className="text-white font-semibold mb-3">Layout Controls (Address Block)</h4>
                <p className="text-sm text-white mb-3">
                  Adjust the address block position, size and font for this specific user only.
                </p>
                <div className="mt-6 border-t border-gray-700 pt-4">
                  <h4 className="text-white font-semibold mb-2">Address Text (This Certificate Only)</h4>
                  <p className="text-sm text-white mb-3">
                    Fix commas or line breaks here if the address is wrapping badly on the certificate. Changes apply
                    only to this certificate and do not change the user's KYC record.
                  </p>
                  {addressOverrideLoading ? (
                    <div className="text-sm text-gray-300">Loading address...</div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-300 mb-1">Address line 1</label>
                          <textarea
                            value={addressOverride?.address_line1 ?? ''}
                            onChange={(e) =>
                              setAddressOverride((prev) => ({
                                address_line1: e.target.value,
                                address_line2: prev?.address_line2 ?? '',
                                city: prev?.city ?? '',
                                country: prev?.country ?? '',
                                postal_code: prev?.postal_code ?? ''
                              }))
                            }
                            rows={2}
                            className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-yellow-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-300 mb-1">Address line 2</label>
                          <textarea
                            value={addressOverride?.address_line2 ?? ''}
                            onChange={(e) =>
                              setAddressOverride((prev) => ({
                                address_line1: prev?.address_line1 ?? '',
                                address_line2: e.target.value,
                                city: prev?.city ?? '',
                                country: prev?.country ?? '',
                                postal_code: prev?.postal_code ?? ''
                              }))
                            }
                            rows={2}
                            className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-yellow-500"
                          />
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm text-gray-300 mb-1">City</label>
                            <input
                              type="text"
                              value={addressOverride?.city ?? ''}
                              onChange={(e) =>
                                setAddressOverride((prev) => ({
                                  address_line1: prev?.address_line1 ?? '',
                                  address_line2: prev?.address_line2 ?? '',
                                  city: e.target.value,
                                  country: prev?.country ?? '',
                                  postal_code: prev?.postal_code ?? ''
                                }))
                              }
                              className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-yellow-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-300 mb-1">Country</label>
                            <input
                              type="text"
                              value={addressOverride?.country ?? ''}
                              onChange={(e) =>
                                setAddressOverride((prev) => ({
                                  address_line1: prev?.address_line1 ?? '',
                                  address_line2: prev?.address_line2 ?? '',
                                  city: prev?.city ?? '',
                                  country: e.target.value,
                                  postal_code: prev?.postal_code ?? ''
                                }))
                              }
                              className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-yellow-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-300 mb-1">Postal code</label>
                            <input
                              type="text"
                              value={addressOverride?.postal_code ?? ''}
                              onChange={(e) =>
                                setAddressOverride((prev) => ({
                                  address_line1: prev?.address_line1 ?? '',
                                  address_line2: prev?.address_line2 ?? '',
                                  city: prev?.city ?? '',
                                  country: prev?.country ?? '',
                                  postal_code: e.target.value
                                }))
                              }
                              className="w-full px-2 py-1 bg-gray-900 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-yellow-500"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        {addressOverrideStatus && (
                          <p className="text-[11px] text-white pr-3">{addressOverrideStatus}</p>
                        )}
                        <button
                          type="button"
                          onClick={handleSaveAddressOverride}
                          disabled={addressOverrideSaving || addressOverrideLoading || !addressOverride}
                          className={`px-3 py-1 text-sm rounded transition-colors ${
                            addressOverrideSaving || addressOverrideLoading || !addressOverride
                              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                              : 'bg-yellow-600 hover:bg-yellow-500 text-white'
                          }`}
                        >
                           Save Address Override
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <UserCertificateLayoutEditor
                  userId={layoutEditorCertificate.user_id}
                  onLayoutSaved={() => refreshLayoutPreview(layoutEditorCertificate)}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-900 border-t border-gray-700 p-4 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <p className="text-[11px] text-white max-w-xl">
                Address changes are saved automatically and preview updates in real-time.
                Click <span className="font-semibold text-white">"Save Address Override"</span> to persist changes to the database.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (layoutEditorCertificate) {
                      // Regenerate PNG with current settings
                      const result = await svgCertificateGenerator.regenerateExistingCertificatePNG({
                        certificateNumber: layoutEditorCertificate.cert_no || layoutEditorCertificate.certificate_number,
                        sharesCount: layoutEditorCertificate.shares_count,
                        purchaseId: layoutEditorCertificate.purchase_id,
                        userId: layoutEditorCertificate.user_id,
                        issueDate: layoutEditorCertificate.issue_date as string | null
                      });

                      if (result.pngUrl) {
                        alert('✅ Certificate PNG regenerated and saved successfully!');
                        await loadData(); // Refresh the list
                      } else {
                        alert('❌ Failed to regenerate certificate PNG');
                      }
                    }
                  }}
                  className="px-4 py-2 text-sm rounded transition-colors bg-green-600 hover:bg-green-500 text-white"
                >
                  ♻️ Regenerate Certificate PNG
                </button>
                <button
                  onClick={() => {
                    setShowLayoutEditor(false);
                    setLayoutEditorCertificate(null);
                    setAddressOverride(null);
                    setAddressOverrideStatus(null);
                    setAddressOverrideLoading(false);
                    setAddressOverrideSaving(false);
                  }}
                  className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Reset All Certificates Modal */}
      {showResetAllModal && (
        <div className='fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4'>
          <div className='bg-gray-900 rounded-lg max-w-xl w-full p-6 border border-red-700 shadow-2xl max-h-[90vh] overflow-y-auto'>
            <h3 className='text-xl font-bold text-red-400 mb-3 flex items-center space-x-2'>
              <span>⚠️</span>
              <span>Reset All Certificates</span>
            </h3>
            <p className='text-white mb-3'>
              This will permanently remove all certificate records and files and reset the certificate counters.
            </p>
            <ul className='text-sm text-gray-300 space-y-1 mb-4 list-disc list-inside'>
              <li>Delete all certificate records from the database</li>
              <li>
                Delete all certificate PNG files from the <span className='font-mono'>certificates</span> storage bucket
              </li>
              <li>Reset certificate numbering counters back to their initial state</li>
            </ul>
            <div className='bg-gray-800 border border-red-700/60 rounded-md p-3 mb-4 text-sm text-red-100'>
              <p className='font-semibold mb-1'>This action cannot be undone.</p>
              <p>
                If you only need to fix a specific certificate, cancel and use the per-certificate delete instead.
              </p>
            </div>
            <div className='mb-3'>
              <label className='block text-sm font-medium text-gray-300 mb-1'>
                Type <span className='font-mono bg-gray-800 px-2 py-0.5 rounded border border-gray-700'>RESET ALL CERTIFICATES</span> to confirm:
              </label>
              <input
                type='text'
                value={resetAllConfirmText}
                onChange={(e) => setResetAllConfirmText(e.target.value)}
                disabled={resetAllInProgress}
                placeholder='RESET ALL CERTIFICATES'
                className='w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500'
              />
            </div>
            {resetAllStatusMessage && (
              <div className='mb-4 text-sm text-white'>
                {resetAllStatusMessage}
              </div>
            )}
            <div className='flex justify-end space-x-3 mt-4'>
              <button
                onClick={() => {
                  if (resetAllInProgress) return;
                  setShowResetAllModal(false);
                  setResetAllConfirmText('');
                  setResetAllStatusMessage(null);
                }}
                disabled={resetAllInProgress}
                className='px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                onClick={resetAllCertificates}
                disabled={
                  resetAllInProgress || resetAllConfirmText.trim() !== 'RESET ALL CERTIFICATES'
                }
                className='px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors font-semibold disabled:opacity-50'
              >
                {resetAllInProgress ? '⏳ Resetting...' : 'Yes, reset all certificates'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Freeze Certificates Modal */}
      {showFreezeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[70] p-4">
          <div className="bg-gray-900 rounded-lg max-w-xl w-full p-6 border border-blue-700 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-blue-300 mb-3 flex items-center space-x-2">
              <span>❄️</span>
              <span>Freeze Certificates for User</span>
            </h3>
            <p className="text-white mb-4 text-sm">
              Search for a user by username, select them, and provide a reason. This will freeze all of their
              certificates so they cannot be viewed or downloaded by the user until they are unfrozen.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">Username</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={freezeUsernameQuery}
                  onChange={(e) => {
                    setFreezeUsernameQuery(e.target.value);
                    setFreezeStatusMessage(null);
                  }}
                  placeholder="Enter username..."
                  className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={searchUsersForFreeze}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                >
                  Search
                </button>
              </div>
            </div>

            {freezeSearchResults.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto border border-gray-700 rounded">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left">Select</th>
                      <th className="px-3 py-2 text-left">Username</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freezeSearchResults.map((user: any) => (
                      <tr
                        key={user.id}
                        className={`cursor-pointer hover:bg-gray-800 ${
                          freezeSelectedUser?.id === user.id ? 'bg-blue-900/40' : ''
                        }`}
                        onClick={() => setFreezeSelectedUser(user)}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="radio"
                            checked={freezeSelectedUser?.id === user.id}
                            onChange={() => setFreezeSelectedUser(user)}
                          />
                        </td>
                        <td className="px-3 py-2 text-white">{user.username || '—'}</td>
                        <td className="px-3 py-2 text-gray-300">{user.full_name || '—'}</td>
                        <td className="px-3 py-2 text-white">{user.email || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Freeze Reason <span className="text-white text-sm">(this will be shown to the user)</span>
              </label>
              <textarea
                value={freezeReason}
                onChange={(e) => setFreezeReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Example: Deposit paid, outstanding balance of R10,000 still due before certificates can be used."
              />
            </div>

            {freezeStatusMessage && (
              <div className="mb-4 text-sm text-white">
                {freezeStatusMessage}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  if (freezeInProgress) return;
                  setShowFreezeModal(false);
                  setFreezeUsernameQuery('');
                  setFreezeSearchResults([]);
                  setFreezeSelectedUser(null);
                  setFreezeReason('');
                  setFreezeStatusMessage(null);
                }}
                disabled={freezeInProgress}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={freezeCertificatesForSelectedUser}
                disabled={freezeInProgress || !freezeSelectedUser || !freezeReason.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors font-semibold disabled:opacity-50"
              >
                {freezeInProgress ? 'Freezing...' : '❄️ Freeze Certificates'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Approval Confirmation Dialog */}
      {showApprovalConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">✓ Approve Certificate?</h3>
            <p className="text-gray-300 mb-6">
              Are you sure you want to approve this certificate? Once approved:
            </p>
            <ul className="text-white text-sm space-y-2 mb-6 ml-4">
              <li>✓ Certificate will be visible to the user</li>
              <li>✓ Email notification will be sent to {viewingCertificate?.user_email}</li>
              <li>✓ User can download their certificate</li>
            </ul>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowApprovalConfirm(false)}
                disabled={processingApproval}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={approveCertificate}
                disabled={processingApproval}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors font-semibold disabled:opacity-50"
              >
                {processingApproval ? '⏳ Approving...' : '✓ Confirm Approval'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">✗ Reject Certificate</h3>
            <p className="text-gray-300 mb-4">
              Please provide a reason for rejecting this certificate:
            </p>

            <div className="mb-4">
              <label className="block text-white text-sm mb-2">Rejection Reason *</label>
              <select
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-yellow-500 mb-3"
              >
                <option value="">Select a reason...</option>
                <option value="Incorrect user information">Incorrect user information</option>
                <option value="Wrong share count">Wrong share count</option>
                <option value="Invalid certificate number">Invalid certificate number</option>
                <option value="Formatting errors">Formatting errors</option>
                <option value="Missing information">Missing information</option>
                <option value="Duplicate certificate">Duplicate certificate</option>
                <option value="Other">Other</option>
              </select>

              {rejectionReason === 'Other' && (
                <textarea
                  placeholder="Please specify the reason..."
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500"
                  rows={3}
                />
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRejectionModal(false);
                  setRejectionReason('');
                }}
                disabled={processingApproval}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectCertificate}
                disabled={processingApproval || !rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
              >
                {processingApproval ? '⏳ Rejecting...' : '✗ Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateManagement;
